import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { Box, Button, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Link } from "react-router-dom";
import type { StoreAuditSummaryRangeDayModel, StoreBackstockLedgerDayModel } from "models";
import type { AuditMetricRowData } from "../../types/auditMetrics";
import { auditDashboardTheme, auditToneStyles, getAuditTone } from "../../theme";
import AuditSummaryComparisonTable from "../shared/AuditSummaryComparisonTable";
import AuditDashboardBackstockLink from "../shared/AuditDashboardBackstockLink";
import AuditDetailLottoPayoutDataDialog, {
  type AuditDetailLottoPayoutDataDialogMode,
  type AuditDetailLottoPayoutDataFormValues,
} from "./AuditDetailLottoPayoutDataDialog";
import AuditDetailScratcherSection from "./AuditDetailScratcherSection";
import { useAuditDashboardManualLottoPayoutDataMutation } from "../../hooks/mutations/useAuditDashboardManualLottoPayoutDataMutation";
import { useAuditDashboardManualDayEntry } from "../../hooks/queries/useAuditDashboardManualDayEntry";
import { buildDayInProgressMetricRows, isAuditDayInProgress } from "../../utils/auditDayProgress";
import { getAuditDataAvailability } from "../../utils/auditMissingState";
import { hasScratcherMismatch } from "../../utils/scratcherTableUtils";
import type { DailyCashierShiftNote } from "../../utils/cashierShiftNotes";
import type { ManualSoldOutImpact, ManualSoldOutReport } from "services/scratcherService";
import { padLeftNumeric, parseBarcodeFormat } from "utils/barcodeFormat";
import ManualSoldOutImpactNotice from "./ManualSoldOutImpactNotice";

interface AuditDetailSummarySectionProps {
  day: StoreAuditSummaryRangeDayModel | null;
  storeId?: string | null;
  scratcherAuditWindowText?: string;
  title?: string;
  reportedScratcherDateLabel?: string;
  inventoryStartDate?: string;
  inventoryEndDate?: string;
  showScratcherSection?: boolean;
  hasPosType?: boolean;
  isLottoAuthorized?: boolean;
  backstockLedgerDay?: StoreBackstockLedgerDayModel | null;
  dayStatusSection?: React.ReactNode;
  isRangeSummary?: boolean;
  cashierShiftNotes?: DailyCashierShiftNote[];
  manualSoldOutReport?: ManualSoldOutReport | null;
  manualSoldOutImpact?: ManualSoldOutImpact | null;
  barcodeFormat?: string | null;
}

const MANUAL_LOTTO_PAYOUT_DATA_STORE_ID = "9223a8a0-37cc-4201-9e27-b48c8bd1b09b";

const manualSoldOutLinkSx = {
  color: auditDashboardTheme.colors.blue,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": {
    color: auditDashboardTheme.colors.text,
  },
};

const formatManualSoldOutGamePack = (
  gameId: string | number,
  packId: string | number,
  barcodeFormat?: string | null
) => {
  try {
    const pattern = parseBarcodeFormat(barcodeFormat);
    return {
      game: padLeftNumeric(gameId, pattern.game.length).slice(-pattern.game.length),
      pack: padLeftNumeric(packId, pattern.pack.length).slice(-pattern.pack.length),
    };
  } catch (_error) {
    return { game: String(gameId), pack: String(packId) };
  }
};

const toNullableAmount = (value: string): number | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const toAmountOrNull = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }

  const parsedValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatCurrency = (value: number | null | undefined) =>
  value == null
    ? "-"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

const formatManualExpectedCurrency = (
  manualValue: number | null,
  currentValue: number,
  canShowCurrentValue: boolean
) => {
  if (manualValue != null) {
    const manualText = formatCurrency(manualValue);
    return canShowCurrentValue ? `${manualText} (${formatCurrency(currentValue)})` : manualText;
  }

  return canShowCurrentValue ? formatCurrency(currentValue) : "-";
};

const renderManualExpectedCurrency = (
  manualValue: number | null,
  currentValue: number,
  canShowCurrentValue: boolean,
  editLabel: string,
  onEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void
): React.ReactNode => {
  const expectedText = formatManualExpectedCurrency(manualValue, currentValue, canShowCurrentValue);

  if (manualValue == null || !onEdit) {
    return expectedText;
  }

  const manualText = formatCurrency(manualValue);
  const dailyText = canShowCurrentValue ? ` (${formatCurrency(currentValue)})` : null;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "4px",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        <Box component="span">{manualText}</Box>
        {dailyText ? (
          <Box component="span" sx={{ color: "#222b4599" }}>
            {dailyText}
          </Box>
        ) : null}
      </Box>
      <Tooltip title={editLabel}>
        <IconButton
          type="button"
          size="small"
          aria-label={editLabel}
          onClick={onEdit}
          sx={{
            width: 22,
            height: 22,
            p: "2px",
            flexShrink: 0,
            color: auditDashboardTheme.colors.blue,
            "&:hover": {
              bgcolor: "rgba(44, 108, 160, 0.08)",
            },
          }}
        >
          <EditOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const formatNumber = (value: number | null | undefined) => (value == null ? "-" : value.toLocaleString("en-US"));

const pluralize = (value: number, singular: string, plural: string) => `${formatNumber(value)} ${value === 1 ? singular : plural}`;

const formatVarianceNumber = (value: number | null | undefined) => {
  if (value == null) {
    return "-";
  }

  return value === 0 ? 0 : formatNumber(value);
};

const formatVarianceCurrency = (value: number | null | undefined) => {
  if (value == null) {
    return "-";
  }

  return value === 0 ? 0 : formatCurrency(value);
};

const formatSummaryHeaderVariance = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return null;
  }

  const variance = Number(value);

  if (variance === 0) {
    return { label: "MATCHED", amount: null };
  }

  const prefix = variance > 0 ? "+" : "-";
  const label = variance > 0 ? "SURPLUS" : "DEFICIT";

  return { label, amount: `${prefix}${formatCurrency(Math.abs(variance))}` };
};

const formatBreakdown = (items: Array<{ label: string; value: number }>) =>
  items
    .filter((item) => Math.abs(item.value) >= 0.005)
    .map((item) => `${item.label}: ${formatCurrency(item.value)}`)
    .join(" + ");

const getManualLottoVarianceFallback = ({
  canUseManualFallback,
  manualValue,
  posValue,
  direction,
}: {
  canUseManualFallback: boolean;
  manualValue?: number | null;
  posValue: number;
  direction: "pos-minus-manual" | "manual-minus-pos";
}) => {
  if (!canUseManualFallback || manualValue == null) {
    return null;
  }

  return direction === "pos-minus-manual" ? posValue - manualValue : manualValue - posValue;
};

const buildSummaryRows = (
  day: StoreAuditSummaryRangeDayModel,
  options: {
    hasPosType: boolean;
    isLottoAuthorized: boolean;
    canUseManualLottoPayoutData?: boolean;
    manualLottoSalesAmount?: number | null;
    manualLottoPayoutsAmount?: number | null;
    onEditManualLottoPayoutData?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }
): AuditMetricRowData[] => {
  const totals = day.auditTotals;
  if (!totals?.audit || !totals.pos || !totals.lotto || !totals.reconciliation_variance) {
    return [];
  }

  const availability = getAuditDataAvailability(day, options);
  const payoutsExpected = totals.lotto.payouts_total + totals.lotto.scratcher_free_tickets_paid_amount;
  const lottoSalesBreakdown = formatBreakdown([
    { label: "Draw Sales", value: totals.lotto.lotto_draw_sales_amount },
    { label: "Voucher Issued", value: totals.lotto.lotto_voucher_issued_amount },
    { label: "Gift Cards", value: totals.lotto.lotto_gift_cards_issued_amount },
  ]);
  const lottoPayoutsBreakdown = formatBreakdown([
    { label: "Draw Pays", value: totals.lotto.lotto_draw_payouts_amount },
    { label: "Voucher Redeemed", value: totals.lotto.lotto_voucher_redeemed_amount },
    { label: "Card Trans", value: totals.lotto.lotto_card_trans_redeemed_amount },
    { label: "Scratcher Payouts", value: totals.lotto.scratcher_payouts_amount },
    { label: "Free Tickets", value: totals.lotto.scratcher_free_tickets_paid_amount },
  ]);
  const lottoBreakdownNotes = [
    lottoSalesBreakdown ? `Lotto Sales: ${lottoSalesBreakdown}` : null,
    lottoPayoutsBreakdown ? `Lotto Payouts: ${lottoPayoutsBreakdown}` : null,
  ].filter((note): note is string => Boolean(note));
  const canUseManualLottoVarianceFallback =
    Boolean(options.canUseManualLottoPayoutData) && !availability.hasLottoData && availability.hasPosData;
  const manualLottoSalesVariance = getManualLottoVarianceFallback({
    canUseManualFallback: canUseManualLottoVarianceFallback,
    manualValue: options.manualLottoSalesAmount,
    posValue: totals.pos.lotto_sales,
    direction: "pos-minus-manual",
  });
  const manualPayoutVariance = getManualLottoVarianceFallback({
    canUseManualFallback: canUseManualLottoVarianceFallback,
    manualValue: options.manualLottoPayoutsAmount,
    posValue: totals.pos.payout,
    direction: "manual-minus-pos",
  });
  const lottoSalesVariance = availability.canShowLottoVariance
    ? totals.reconciliation_variance.lotto_sales_variance
    : manualLottoSalesVariance;
  const payoutVariance = availability.canShowLottoVariance
    ? totals.reconciliation_variance.payout_variance
    : manualPayoutVariance;
  const canShowLottoSalesVariance = availability.canShowLottoVariance || manualLottoSalesVariance != null;
  const canShowPayoutVariance = availability.canShowLottoVariance || manualPayoutVariance != null;
  const scratcherQtyVariance = availability.canShowScratcherVariance ? totals.reconciliation_variance.scratcher.quantity_variance : null;
  const scratcherRevenueVariance = availability.canShowScratcherVariance ? totals.reconciliation_variance.scratcher.revenue_variance : null;

  return [
    {
      id: "detail-summary-lotto",
      label: "Lotto Sales",
      expected: renderManualExpectedCurrency(
        options.manualLottoSalesAmount ?? null,
        totals.lotto.sales,
        availability.hasLottoData,
        "Edit Lotto Sales manual entry",
        options.onEditManualLottoPayoutData
      ),
      reported: availability.hasPosData ? formatCurrency(totals.pos.lotto_sales) : "-",
      variance: canShowLottoSalesVariance ? formatVarianceCurrency(lottoSalesVariance) : "-",
      tone: canShowLottoSalesVariance ? getAuditTone(lottoSalesVariance) : "progress",
    },
    {
      id: "detail-summary-payouts",
      label: "Lotto Payouts",
      detailNotes: availability.hasLottoData && lottoBreakdownNotes.length > 0 ? lottoBreakdownNotes : undefined,
      expected: renderManualExpectedCurrency(
        options.manualLottoPayoutsAmount ?? null,
        payoutsExpected,
        availability.hasLottoData,
        "Edit Lotto Payout manual entry",
        options.onEditManualLottoPayoutData
      ),
      reported: availability.hasPosData ? formatCurrency(totals.pos.payout) : "-",
      variance: canShowPayoutVariance ? formatVarianceCurrency(payoutVariance) : "-",
      tone: canShowPayoutVariance ? getAuditTone(payoutVariance) : "progress",
      dividerAfter: "strong",
    },
    {
      id: "detail-summary-scratcher-qty",
      label: "Scratcher Qty",
      expected: availability.isAuditMissing ? "-" : formatNumber(totals.audit.total_sold),
      reported: availability.hasPosData ? formatNumber(totals.pos.scratcher_count) : "-",
      variance: availability.canShowScratcherVariance ? formatVarianceNumber(scratcherQtyVariance) : "-",
      tone: availability.canShowScratcherVariance ? getAuditTone(scratcherQtyVariance) : "progress",
    },
    {
      id: "detail-summary-scratcher-sales",
      label: "Scratcher $",
      expected: availability.isAuditMissing ? "-" : formatCurrency(totals.audit.total_revenue),
      reported: availability.hasPosData ? formatCurrency(totals.pos.scratcher_sales) : "-",
      variance: availability.canShowScratcherVariance ? formatVarianceCurrency(scratcherRevenueVariance) : "-",
      tone: availability.canShowScratcherVariance ? getAuditTone(scratcherRevenueVariance) : "progress",
      hideBottomBorder: true,
    },
  ];
};

export default function AuditDetailSummarySection({
  day,
  storeId,
  scratcherAuditWindowText = "-",
  title,
  reportedScratcherDateLabel,
  inventoryStartDate,
  inventoryEndDate,
  showScratcherSection = true,
  hasPosType = true,
  isLottoAuthorized = true,
  backstockLedgerDay = null,
  dayStatusSection,
  isRangeSummary = false,
  cashierShiftNotes = [],
  manualSoldOutReport = null,
  manualSoldOutImpact = null,
  barcodeFormat,
}: AuditDetailSummarySectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [lottoPayoutDialogOpen, setLottoPayoutDialogOpen] = useState(false);
  const [lottoPayoutDialogMode, setLottoPayoutDialogMode] = useState<AuditDetailLottoPayoutDataDialogMode>("add");
  const scratcherDetailsId = React.useId();
  const summaryDate = day ? moment(day.reportDate).format("ddd, M/D/YYYY") : "";
  const businessDate = day?.reportDate ? moment(day.reportDate).format("YYYY-MM-DD") : null;
  const isManualLottoPayoutDataStore = storeId?.toLowerCase() === MANUAL_LOTTO_PAYOUT_DATA_STORE_ID;
  const canUseManualLottoPayoutData = !isRangeSummary && isManualLottoPayoutDataStore;
  const manualDayEntryQuery = useAuditDashboardManualDayEntry(
    storeId,
    businessDate,
    canUseManualLottoPayoutData
  );
  const manualLottoPayoutDataMutation = useAuditDashboardManualLottoPayoutDataMutation(storeId, businessDate);
  const reportedScratcherDate = reportedScratcherDateLabel ?? (day ? moment(day.reportDate).format("M/DD") : "");
  const isDayInProgress = isAuditDayInProgress(day?.reportDate);
  const availability = useMemo(
    () => getAuditDataAvailability(day, { hasPosType, isLottoAuthorized, isDayInProgress }),
    [day, hasPosType, isDayInProgress, isLottoAuthorized]
  );
  const reportedScratcherQty = formatNumber(day?.auditTotals?.pos?.scratcher_count ?? 0);
  const reportedScratcherSales = formatCurrency(day?.auditTotals?.pos?.scratcher_sales ?? 0);
  const backstockText = backstockLedgerDay
    ? `${pluralize(backstockLedgerDay.closingTickets, "ticket", "tickets")} | ${pluralize(backstockLedgerDay.closingPacks, "pack", "packs")}`
    : "-";
  const manualSoldOutText = manualSoldOutReport?.pack_count
    ? `${pluralize(manualSoldOutReport.total_tickets, "ticket", "tickets")} | ${pluralize(manualSoldOutReport.pack_count, "pack", "packs")} | ${formatCurrency(Number(manualSoldOutReport.total_retail_value))}`
    : "-";
  const hasManualSoldOutPacks = Boolean(manualSoldOutReport?.packs.length);
  const totalVariance = day?.auditTotals?.reconciliation_variance?.total_variance;
  const scratcherVariance = day?.auditTotals?.reconciliation_variance?.scratcher;
  const isScratcherMismatch = Boolean(
    !isRangeSummary
    && hasPosType
    && availability.canShowScratcherVariance
    && hasScratcherMismatch(
      scratcherVariance?.quantity_variance,
      scratcherVariance?.revenue_variance
    )
  );
  const isSummaryExpanded = isScratcherMismatch || expanded;
  const hasMissingSources = availability.hasMissingSources;
  const summaryTone = isDayInProgress || hasMissingSources ? "progress" : getAuditTone(totalVariance);
  const summaryToneStyle = auditToneStyles[summaryTone];
  const summaryRadius = `${auditDashboardTheme.shape.containerRadius}px`;
  const summaryVarianceLabel = hasMissingSources
    ? { label: "AWAITED", amount: null }
    : formatSummaryHeaderVariance(totalVariance);
  const summaryReportToggleLabel = isScratcherMismatch
    ? "Summary report stays open while scratchers are mismatched"
    : isSummaryExpanded
      ? "Close summary report"
      : "Open summary report";
  const manualLottoSalesAmount = toAmountOrNull(manualDayEntryQuery.data?.lotto_sales_amount);
  const manualLottoPayoutsAmount = toAmountOrNull(manualDayEntryQuery.data?.lotto_payouts_amount);
  const hasManualLottoPayoutData = manualLottoSalesAmount != null || manualLottoPayoutsAmount != null;
  const isManualDayEntryLoading = canUseManualLottoPayoutData && manualDayEntryQuery.isLoading;
  const showAddLottoPayoutDataButton =
    canUseManualLottoPayoutData && !isManualDayEntryLoading && !hasManualLottoPayoutData;
  const manualLottoPayoutDialogInitialValues = useMemo<AuditDetailLottoPayoutDataFormValues>(
    () => ({
      lottoSales: manualDayEntryQuery.data?.lotto_sales_amount != null
        ? String(manualDayEntryQuery.data.lotto_sales_amount)
        : "",
      lottoPayout: manualDayEntryQuery.data?.lotto_payouts_amount != null
        ? String(manualDayEntryQuery.data.lotto_payouts_amount)
        : "",
    }),
    [manualDayEntryQuery.data?.lotto_payouts_amount, manualDayEntryQuery.data?.lotto_sales_amount]
  );

  const handleOpenAddLottoPayoutDialog = useCallback(() => {
    setLottoPayoutDialogMode("add");
    setLottoPayoutDialogOpen(true);
  }, []);

  const handleOpenUpdateLottoPayoutDialog = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setLottoPayoutDialogMode("update");
    setLottoPayoutDialogOpen(true);
  }, []);

  const handleCloseLottoPayoutDialog = () => {
    setLottoPayoutDialogOpen(false);
  };

  const handleSaveLottoPayoutDialog = async (values: AuditDetailLottoPayoutDataFormValues) => {
    if (!businessDate) {
      return;
    }

    try {
      await manualLottoPayoutDataMutation.mutateAsync({
        business_date: businessDate,
        lotto_sales_amount: toNullableAmount(values.lottoSales),
        lotto_payouts_amount: toNullableAmount(values.lottoPayout),
        cash_collection_amount: null,
        credit_card_collection_amount: null,
      });

      setLottoPayoutDialogOpen(false);
    } catch {
      // Error toast is handled by the mutation.
    }
  };

  const rows = useMemo(() => {
    if (!day) {
      return [];
    }

    const canShowDayInProgressScratcherExpectedValues =
      isDayInProgress && (day.auditTotals?.audit?.audit_count ?? 0) > 0;
    const summaryRows = buildSummaryRows(day, {
      hasPosType,
      isLottoAuthorized,
      canUseManualLottoPayoutData,
      manualLottoSalesAmount,
      manualLottoPayoutsAmount,
      onEditManualLottoPayoutData: canUseManualLottoPayoutData ? handleOpenUpdateLottoPayoutDialog : undefined,
    });
    return isDayInProgress
      ? buildDayInProgressMetricRows(summaryRows, {
          preserveScratcherExpectedValues: canShowDayInProgressScratcherExpectedValues,
        })
      : summaryRows;
  }, [
    canUseManualLottoPayoutData,
    day,
    handleOpenUpdateLottoPayoutDialog,
    hasPosType,
    isDayInProgress,
    isLottoAuthorized,
    manualLottoPayoutsAmount,
    manualLottoSalesAmount,
  ]);

  if (!day) {
    return null;
  }

  return (
    <Box
      component="section"
      sx={{
        border: "1px solid",
        borderColor: summaryToneStyle.border,
        borderRadius: `${summaryRadius} !important`,
        bgcolor: auditDashboardTheme.colors.surface,
        backgroundImage: "none",
        boxShadow: "none",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            py: 0,
            borderBottom: "1px solid",
            borderColor: auditDashboardTheme.colors.line,
            borderTopLeftRadius: summaryRadius,
            borderTopRightRadius: summaryRadius,
            bgcolor: summaryToneStyle.bg,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              alignItems: "center",
              gap: "12px",
              px: { xs: 1.75, sm: 3 },
              py: "12px",
              minWidth: 0,
            }}
          >
            <Typography
              component="span"
              sx={{
                display: "block",
                minWidth: 0,
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.15,
                color: auditDashboardTheme.colors.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title ?? `Summary of ${summaryDate}`}
            </Typography>
            <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", minWidth: 0 }}>
              {showAddLottoPayoutDataButton ? (
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  onClick={handleOpenAddLottoPayoutDialog}
                  sx={{
                    flexShrink: 0,
                    minHeight: 28,
                    px: 1.25,
                    py: 0.25,
                    borderRadius: "4px",
                    borderColor: auditDashboardTheme.colors.blue,
                    color: auditDashboardTheme.colors.blue,
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      borderColor: auditDashboardTheme.colors.blue,
                      bgcolor: "rgba(44, 108, 160, 0.08)",
                    },
                  }}
                >
                  + Add Lotto/Payout Data
                </Button>
              ) : null}
              {summaryVarianceLabel ? (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 13,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <Box component="span" sx={{ fontSize: 16, color: summaryToneStyle.text, fontWeight: 700 }}>
                    {summaryVarianceLabel.label}
                  </Box>
                  {summaryVarianceLabel.amount ? (
                    <Box component="span" sx={{ color: summaryToneStyle.text, fontSize: 15, fontWeight: 700 }}>
                      {` ${summaryVarianceLabel.amount}`}
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
              {showScratcherSection ? (
                <Tooltip title={summaryReportToggleLabel}>
                  <IconButton
                    size="small"
                    disableRipple
                    aria-label={summaryReportToggleLabel}
                    aria-expanded={isSummaryExpanded}
                    aria-controls={scratcherDetailsId}
                    aria-disabled={isScratcherMismatch}
                    onClick={() => {
                      if (!isScratcherMismatch) {
                        setExpanded((current) => !current);
                      }
                    }}
                    sx={{
                      width: 30,
                      height: 30,
                      p: "4px",
                      color: auditDashboardTheme.colors.blue,
                      flexShrink: 0,
                      "&:hover": {
                        bgcolor: "rgba(44, 108, 160, 0.08)",
                      },
                    }}
                  >
                    <ChevronRightIcon
                      aria-hidden="true"
                      sx={{
                        fontSize: 22,
                        transform: isSummaryExpanded ? "rotate(90deg)" : "none",
                        transition: "transform 160ms ease",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, pb: dayStatusSection ? 0 : "8px", bgcolor: auditDashboardTheme.colors.surface }}>
          <Box sx={{ minWidth: 0, px: { xs: 1.75, sm: 3 } }}>
            <AuditSummaryComparisonTable rows={rows} variant="detail" />
          </Box>

          <Box
            sx={{
              px: { xs: 1.75, sm: 3 },
              pt: "8px",
              pb: "2px",
            }}
          >
            <Box
              sx={{
                py: "4px",
                display: "grid",
                gap: "8px",
                minWidth: 0,
              }}
            >
              {manualSoldOutImpact?.packs.length ? (
                <ManualSoldOutImpactNotice
                  impact={manualSoldOutImpact}
                  storeId={storeId}
                  barcodeFormat={barcodeFormat}
                />
              ) : null}
              {hasManualSoldOutPacks ? (
                <Box
                aria-label="Manual sold out details"
                sx={{
                  display: "grid",
                  gap: "8px",
                  px: "10px",
                  py: "8px",
                  border: "1px solid rgba(180, 83, 9, 0.18)",
                  borderRadius: "8px",
                  bgcolor: "rgba(255, 247, 237, 0.65)",
                  overflowX: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
                    Manual Sold Out
                  </Typography>
                  <Typography sx={{ color: "#b45309", fontSize: 15, fontWeight: 500, lineHeight: 1.2, whiteSpace: "nowrap", textAlign: "right" }}>
                    {manualSoldOutText}
                  </Typography>
                </Box>
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    color: auditDashboardTheme.colors.textMutedStrong,
                    fontSize: 13,
                    fontWeight: 400,
                    lineHeight: 1.35,
                  }}
                >
                  These packs were manually marked sold out today and affect the past audit dates shown below.
                </Typography>
                {manualSoldOutReport?.packs.length ? (
                  <Box sx={{ display: "grid", gap: "5px", minWidth: 760 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(320px, 1fr) 130px 72px 88px 92px",
                      gap: 1,
                      color: auditDashboardTheme.colors.textMutedStrong,
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    <Box>Game</Box><Box>Pack</Box><Box sx={{ textAlign: "right" }}>Tickets</Box>
                    <Box sx={{ textAlign: "right" }}>Value</Box><Box sx={{ textAlign: "right" }}>Affects</Box>
                  </Box>
                  {manualSoldOutReport.packs.map((pack) => {
                    const formattedGamePack = formatManualSoldOutGamePack(pack.game_id, pack.pack_id, barcodeFormat);

                    return (
                      <Box
                        key={pack.audit_detail_id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(320px, 1fr) 130px 72px 88px 92px",
                          gap: 1,
                          alignItems: "center",
                          color: auditDashboardTheme.colors.text,
                          fontSize: 15,
                          lineHeight: 1.35,
                        }}
                      >
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, whiteSpace: "nowrap" }}>
                        <Box
                          component={Link}
                          to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle`}
                          sx={{ ...manualSoldOutLinkSx, fontSize: 15, fontWeight: 500, lineHeight: 1.35 }}
                        >
                          {pack.game_name || `Game ${pack.game_id}`}
                        </Box>
                        <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>•</Box>
                        <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 15 }}>{pack.tickets_per_pack == null ? "- tickets" : `${formatNumber(pack.tickets_per_pack)} tickets`}</Box>
                        <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>•</Box>
                        <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 15 }}>{formatCurrency(Number(pack.pack_value))}</Box>
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          color: auditDashboardTheme.colors.textMutedStrong,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important',
                          fontSize: 15,
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                          "& *": { fontFamily: "inherit !important" },
                        }}
                      >
                        <Box component={Link} to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle`} sx={manualSoldOutLinkSx}>{formattedGamePack.game}</Box>
                        <Box component="span">-</Box>
                        <Box component={Link} to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle/${pack.pack_id}?sold_out=true`} sx={manualSoldOutLinkSx}>{formattedGamePack.pack}</Box>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>{formatNumber(pack.tickets)}</Box>
                      <Box sx={{ textAlign: "right" }}>{formatCurrency(Number(pack.retail_value))}</Box>
                      <Box
                        component={Link}
                        to={`/store/${storeId}?from=${pack.affected_date}&days=1&report_date=${pack.affected_date}`}
                        sx={{ ...manualSoldOutLinkSx, textAlign: "right" }}
                      >
                        {moment(pack.affected_date).format("M/D/YY")}
                      </Box>
                      </Box>
                    );
                  })}
                  </Box>
                ) : null}
                </Box>
              ) : null}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
                  alignItems: "center",
                  gap: { xs: "4px", md: 2 },
                  minWidth: 0,
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    fontSize: 13,
                    fontWeight: 400,
                    lineHeight: 1.3,
                    color: auditDashboardTheme.colors.textMutedStrong,
                    minWidth: 0,
                    overflowWrap: "anywhere",
                  }}
                >
                  <Box component="strong" sx={{ fontWeight: 500 }}>
                    Scratcher Audit Window:
                  </Box>{" "}
                  <Box component="span" sx={{ fontStyle: "italic" }}>
                    {scratcherAuditWindowText}
                  </Box>
                </Typography>
                <AuditDashboardBackstockLink
                  reportDate={inventoryStartDate && inventoryEndDate ? undefined : day.reportDate}
                  startDate={inventoryStartDate}
                  endDate={inventoryEndDate}
                  sx={{
                    textAlign: { xs: "left", md: "right" },
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                  }}
                >
                  <Typography component="div" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 13, fontWeight: 400, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>Live Backstock:</Box>{" "}
                    <Box component="span" sx={{ color: "#df9400" }}>{backstockText}</Box>
                  </Typography>
                </AuditDashboardBackstockLink>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
                  alignItems: "center",
                  gap: { xs: "4px", md: 2 },
                  minWidth: 0,
                  pt: "8px",
                  borderTop: "1px solid",
                  borderColor: auditDashboardTheme.colors.lineStrong,
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    color: auditDashboardTheme.colors.textMutedStrong,
                    whiteSpace: "nowrap",
                  }}
                >
                  {`Reported (${reportedScratcherDate})`}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    color: auditDashboardTheme.colors.textMutedStrong,
                    whiteSpace: "nowrap",
                    textAlign: { xs: "left", md: "right" },
                  }}
                >
                  {`${reportedScratcherQty} tickets | ${reportedScratcherSales}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <AuditDetailLottoPayoutDataDialog
        open={lottoPayoutDialogOpen}
        mode={lottoPayoutDialogMode}
        initialValues={manualLottoPayoutDialogInitialValues}
        isSaving={manualLottoPayoutDataMutation.isPending}
        onClose={handleCloseLottoPayoutDialog}
        onSave={handleSaveLottoPayoutDialog}
      />

      {dayStatusSection ? (
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "rgba(148, 163, 184, 0.18)",
          }}
        >
          {dayStatusSection}
        </Box>
      ) : null}

      <Collapse in={showScratcherSection && isSummaryExpanded} timeout="auto" unmountOnExit>
        <Box id={scratcherDetailsId} role="region" aria-label="Scratcher details" sx={{ p: 0, borderTop: 0, bgcolor: auditDashboardTheme.colors.surface }}>
          <AuditDetailScratcherSection
            embedded
            reportDate={inventoryStartDate && inventoryEndDate ? undefined : day.reportDate}
            startDate={inventoryStartDate}
            endDate={inventoryEndDate}
            defaultTab={isScratcherMismatch ? "mismatched" : "all"}
            cashierShiftNotes={cashierShiftNotes}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
